import styled, { css } from 'styled-components';

import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import type { HeaderThemeKeys } from '@/theme/types/components';

export const Nav = styled.nav<{
  $headerBackground: HeaderThemeKeys;
  $isShrunk: boolean;
  $searchOverlay: string;
  $textColorOption: string;
}>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  padding-top: 10px;

  ${({
    $headerBackground,
    $isShrunk,
    $searchOverlay,
    $textColorOption,
    theme: {
      breakpoints,
      colors,
      components: {
        header: { breakpoint: headerBreakpoint, megamenu, theme: headerTheme },
        navigation,
      },
      fonts,
    },
  }) => css`
    @media (max-width: ${breakpoints.md}) {
      margin-left: 0;
      grid-column: 1 / span 3;
      display: none;
    }

    & > ul {
      display: flex;
      align-items: center;
      gap: ${navigation.list.gap};
      overflow-x: auto;
      scrollbar-width: none;

      &::-webkit-scrollbar {
        display: none;
      }

      @media (max-width: ${headerBreakpoint}) {
        padding-right: ${navigation.list.paddingRight};
      }
    }

    .mainMenuItemLink {
      font-family: ${fonts.subtitleFont};
      font-size: ${megamenu.fontSize};
      text-decoration: none;
      transition: color 0.2s;
      text-transform: uppercase;
      white-space: nowrap;

      &:hover {
        color: ${headerTheme[$headerBackground].hover.text.color};
      }

      &:active {
        color: ${headerTheme[$headerBackground].active.text.color};
      }

      ${$isShrunk || $textColorOption === 'dark' || $searchOverlay === 'OPEN'
        ? css`
            color: ${headerTheme[$headerBackground].default.menu.color};
          `
        : css`
            color: ${headerTheme[$headerBackground].default.menu.color};
          `}
    }

    .itemGroupTitle {
      color: ${colors.almostBlack};
      font-family: ${fonts.subtitleFont};
      font-size: 15px;
      text-decoration: none;
      transition: color 0.2s;
      text-transform: uppercase;
      white-space: nowrap;
    }
  `}
`;

export const MainMenuLink = styled(LinkWithLocale)`
  line-height: ${({ theme }) => theme.spacing(2.75)};
`;

export const Item = styled.div<{
  $headerBackground: HeaderThemeKeys;
  $hideDecoration?: boolean;
  $isHover?: boolean;
}>`
  padding-bottom: 3px;
  position: relative;

  ${({
    $headerBackground,
    $hideDecoration,
    theme: {
      components: {
        header: { theme: headerTheme },
      },
    },
  }) =>
    !$hideDecoration &&
    css`
      &::after {
        content: '';
        display: block;
        width: 0;
        height: 2px;
        background: ${headerTheme[$headerBackground].hover.headerInfoText
          .color};
        transition: width 0.5s;
        position: absolute;
        right: 0;
      }
    `}

  ${({ $hideDecoration, $isHover, theme }) =>
    !$hideDecoration &&
    $isHover &&
    css`
      &::after {
        width: 100%;
        transition: width 0.5s;
        left: 0;
      }
      font-weight: ${theme.fontWeights.semibold};
    `}
`;

export const SubmenuContainer = styled.div<{
  $isActive: boolean;
}>`
  display: none;
  max-width: 90rem;
  margin: 0 auto;

  ${({ $isActive }) =>
    $isActive &&
    css`
      display: block;
    `}
`;

export const Submenu = styled.div<{
  top: number;
}>`
  position: fixed;
  width: 100%;
  background: white;
  left: 0;
  ${({ top }) => css`
    top: calc(
      ${top}px - 3px
    ); // - 3px: Overlap the Megamenu and the header to hide the space between the Megamenu and the header.
  `}
`;

export const Title = styled.div`
  cursor: pointer;
`;
